// Admin → Processing → Dashboard. One-fetch overview of Quotes, Orders,
// Purchase Orders, Abandoned Carts, and Payments — the five sibling pages
// under Processing. Acts as the section's landing view so admins get a
// bird's-eye before drilling into any one page.
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch } from '../../lib/api'
import { StatusBadge } from './Overview'
import {
  BarChart, DonutChart, LineChart, StackedBar, KpiTile, STATUS_COLOURS, fmtCents,
} from '../../components/admin/Charts'
import DateRangeFilter, { RangeValue } from '../../components/admin/DateRangeFilter'

interface KpiBucket { orders: number; revenueCents: number }
interface StatusRow { status: string; count: number; totalCents: number }
interface TimelineRow { day: string; orders: number; revenueCents: number }
interface PaymentRow { day: string; capturedCents: number; refundedCents: number }
interface AgingRow { bucket: string; count: number; totalCents?: number }
interface AbandonedRow { bucket: string; count: number }
interface TopCustomer { email: string | null; name: string | null; orderCount: number; totalCents: number }
interface RecentOrder { id: string; number: number; status: string; totalCents: number; createdAt: string; customerEmail: string | null; customerName: string | null; itemCount: number }
interface DashData {
  kpi: {
    thisWeek: KpiBucket; prevWeek: KpiBucket
    outstandingQuotes: { count: number; totalCents: number }
    abandoned: { count: number }
  }
  ordersByStatus: StatusRow[]
  ordersTimeline: TimelineRow[]
  pendingPaymentAging: AgingRow[]
  quotesByStatus: StatusRow[]
  posByStatus: StatusRow[]
  paymentsTimeline: PaymentRow[]
  abandonedByAge: AbandonedRow[]
  topCustomers: TopCustomer[]
  recentOrders: RecentOrder[]
}

const STATUS_LABELS: Record<string, string> = {
  pending_payment: 'Pending payment', paid: 'Paid', in_production: 'In production',
  shipped: 'Shipped', delivered: 'Delivered', cancelled: 'Cancelled', refunded: 'Refunded',
  draft: 'Draft', sent: 'Sent', accepted: 'Accepted', rejected: 'Rejected', expired: 'Expired',
  received: 'Received',
}
function label(s: string) { return STATUS_LABELS[s] ?? s }

function pctChange(curr: number, prev: number): number {
  if (prev === 0) return curr === 0 ? 0 : 100
  return ((curr - prev) / prev) * 100
}
function shortDay(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z')
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' })
}

export default function ProcessingDashboard() {
  const { getAccessToken } = useAuth()
  const [data, setData] = useState<DashData | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [range, setRange] = useState<RangeValue | null>(null)

  const load = useCallback(async () => {
    if (!range) return
    try {
      const qs = new URLSearchParams({ from: range.fromISO, to: range.toISO })
      const d = await apiFetch<DashData>(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/processing-dashboard?${qs.toString()}`)
      setData(d); setErr(null)
    } catch (e) { setErr((e as Error).message) }
  }, [range, getAccessToken])
  useEffect(() => { void load() }, [load])

  if (err) return <div className="p-8"><div className="card text-red-700">Error: {err}</div></div>
  if (!data) return (
    <div className="p-8 space-y-6">
      <div>
        <div className="eyebrow">Processing</div>
        <h1 className="font-heading uppercase font-bold text-3xl text-ink">Dashboard</h1>
      </div>
      <DateRangeFilter defaultKey="30d" onChange={setRange} />
      <div className="text-ink-3 text-sm">Loading dashboard…</div>
    </div>
  )

  const ordersTimelineX = data.ordersTimeline.map((r) => shortDay(r.day))
  const ordersTimelineOrders = data.ordersTimeline.map((r) => r.orders)
  const ordersTimelineRevenue = data.ordersTimeline.map((r) => r.revenueCents)

  const paymentsX = data.paymentsTimeline.map((r) => shortDay(r.day))
  const paymentsCaptured = data.paymentsTimeline.map((r) => r.capturedCents)
  const paymentsRefunded = data.paymentsTimeline.map((r) => r.refundedCents)

  const ordersDonutData = data.ordersByStatus.map((r) => ({
    label: label(r.status), value: r.count, colour: STATUS_COLOURS[r.status] ?? '#94a3b8',
  }))
  const ordersStackData = data.ordersByStatus.map((r) => ({
    label: label(r.status), value: r.count, colour: STATUS_COLOURS[r.status] ?? '#94a3b8',
  }))
  const quotesBars = data.quotesByStatus.map((r) => ({
    label: label(r.status), value: r.count, colour: STATUS_COLOURS[r.status] ?? '#94a3b8',
  }))
  const posBars = data.posByStatus.map((r) => ({
    label: label(r.status), value: r.count, colour: STATUS_COLOURS[r.status] ?? '#94a3b8',
  }))
  const agingBars = data.pendingPaymentAging.map((r) => ({
    label: r.bucket, value: r.count,
    colour: r.bucket === '30d+' ? '#EF4444' : r.bucket === '7-30d' ? '#F59E0B' : r.bucket === '3-7d' ? '#F59E0B' : '#94a3b8',
  }))
  const abandonedBars = data.abandonedByAge.map((r) => ({
    label: r.bucket, value: r.count,
    colour: r.bucket === '30d+' ? '#9CA3AF' : r.bucket === '7-30d' ? '#A1A1AA' : '#D4D4D8',
  }))

  const totalOrders = data.ordersByStatus.reduce((s, r) => s + r.count, 0)
  const totalRevenue = data.ordersByStatus.reduce((s, r) => s + r.totalCents, 0)
  const weekOrdersDelta = pctChange(data.kpi.thisWeek.orders,  data.kpi.prevWeek.orders)
  const weekRevDelta    = pctChange(data.kpi.thisWeek.revenueCents, data.kpi.prevWeek.revenueCents)

  return (
    <div className="p-8 space-y-6">
      <div>
        <div className="eyebrow">Processing</div>
        <h1 className="font-heading uppercase font-bold text-3xl text-ink">Dashboard</h1>
        <p className="text-ink-2 text-sm mt-1 max-w-2xl">Bird's-eye view of everything moving through Quotes, Orders, Purchase Orders, Abandoned Carts and Payments. Click a tile to drill in.</p>
      </div>

      <DateRangeFilter defaultKey="30d" onChange={setRange} />

      {/* KPI tiles — top of fold */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile
          label="Revenue · 7d"
          value={fmtCents(data.kpi.thisWeek.revenueCents)}
          sub={`${data.kpi.thisWeek.orders} order${data.kpi.thisWeek.orders === 1 ? '' : 's'}`}
          delta={{ value: weekRevDelta, positiveIsGood: true }}
          accent="emerald"
        />
        <KpiTile
          label="Orders · 7d"
          value={String(data.kpi.thisWeek.orders)}
          sub={`vs ${data.kpi.prevWeek.orders} last week`}
          delta={{ value: weekOrdersDelta, positiveIsGood: true }}
          accent="gold"
        />
        <KpiTile
          label="Quote pipeline"
          value={fmtCents(data.kpi.outstandingQuotes.totalCents)}
          sub={`${data.kpi.outstandingQuotes.count} open`}
          hint="Draft + sent — potential revenue"
          accent="blue"
        />
        <KpiTile
          label="Abandoned designs"
          value={String(data.kpi.abandoned.count)}
          sub="idle > 1 hour, no order yet"
          delta={data.kpi.abandoned.count > 0 ? { value: 0 } : undefined}
          accent={data.kpi.abandoned.count > 5 ? 'amber' : 'gold'}
        />
      </div>

      {/* Row: 30-day revenue + orders timeline */}
      <div className="card">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="eyebrow !mb-1">Last 30 days</div>
            <div className="text-ink font-semibold">Revenue &amp; orders by day</div>
          </div>
          <Link to="/admin/orders" className="text-[11px] text-gold-dark hover:underline">All orders →</Link>
        </div>
        {data.ordersTimeline.length === 0 ? (
          <div className="text-[12px] text-ink-3 text-center py-10">No paid orders in the last 30 days yet.</div>
        ) : (
          <LineChart
            series={[
              { label: 'Revenue', data: ordersTimelineRevenue, colour: STATUS_COLOURS.paid },
              { label: 'Orders ×100¢',  data: ordersTimelineOrders.map((n) => n * 100), colour: STATUS_COLOURS.in_production },
            ]}
            xLabels={ordersTimelineX}
            yFormatter={fmtCents}
            height={240}
          />
        )}
      </div>

      {/* Row: Orders by status (donut) + pending-payment aging bars */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="eyebrow !mb-1">Orders</div>
              <div className="text-ink font-semibold">By status</div>
            </div>
            <Link to="/admin/orders" className="text-[11px] text-gold-dark hover:underline">Open →</Link>
          </div>
          {ordersDonutData.length === 0
            ? <div className="text-[12px] text-ink-3 text-center py-10">No orders yet.</div>
            : <DonutChart data={ordersDonutData} centerLabel={String(totalOrders)} centerSub="orders" />}
          <div className="mt-3">
            <StackedBar data={ordersStackData} />
            <div className="flex items-center justify-between mt-1 text-[11px] text-ink-3">
              <span>{totalOrders} orders all-time</span>
              <span className="font-mono">{fmtCents(totalRevenue)} total</span>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="eyebrow !mb-1">Pending payment</div>
              <div className="text-ink font-semibold">How stale is the unpaid pipeline?</div>
            </div>
            <Link to="/admin/orders?status=pending_payment" className="text-[11px] text-gold-dark hover:underline">Chase →</Link>
          </div>
          {data.pendingPaymentAging.every((r) => r.count === 0)
            ? <div className="text-[12px] text-ink-3 text-center py-10">No pending-payment orders — clean!</div>
            : <BarChart data={agingBars} height={200} />}
          <div className="text-[11px] text-ink-3 mt-2">Bucketed on order creation date. Anything in <strong className="text-amber-700">7-30d</strong> or <strong className="text-red-700">30d+</strong> deserves a chase.</div>
        </div>
      </div>

      {/* Row: Quotes + POs */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="eyebrow !mb-1">Quotes</div>
              <div className="text-ink font-semibold">Pipeline by status</div>
            </div>
            <Link to="/admin/quotes" className="text-[11px] text-gold-dark hover:underline">Open →</Link>
          </div>
          {quotesBars.length === 0
            ? <div className="text-[12px] text-ink-3 text-center py-10">No quotes yet.</div>
            : <BarChart data={quotesBars} height={200} />}
          <div className="grid grid-cols-2 gap-2 text-[12px] text-ink-2 mt-3">
            {data.quotesByStatus.map((q) => (
              <div key={q.status} className="flex items-center justify-between gap-2 border-t border-border pt-1.5">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-sm" style={{ background: STATUS_COLOURS[q.status] ?? '#94a3b8' }} />
                  {label(q.status)}
                </span>
                <span className="font-mono text-ink">{fmtCents(q.totalCents)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="eyebrow !mb-1">Purchase orders</div>
              <div className="text-ink font-semibold">Supplier orders by status</div>
            </div>
            <Link to="/admin/purchase-orders" className="text-[11px] text-gold-dark hover:underline">Open →</Link>
          </div>
          {posBars.length === 0
            ? <div className="text-[12px] text-ink-3 text-center py-10">No POs yet.</div>
            : <BarChart data={posBars} height={200} />}
          <div className="grid grid-cols-2 gap-2 text-[12px] text-ink-2 mt-3">
            {data.posByStatus.map((p) => (
              <div key={p.status} className="flex items-center justify-between gap-2 border-t border-border pt-1.5">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-sm" style={{ background: STATUS_COLOURS[p.status] ?? '#94a3b8' }} />
                  {label(p.status)}
                </span>
                <span className="font-mono text-ink">{fmtCents(p.totalCents)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Row: Payments timeline + Abandoned designs by age */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="eyebrow !mb-1">Payments · 30 days</div>
              <div className="text-ink font-semibold">Captured vs refunded</div>
            </div>
            <Link to="/admin/payments" className="text-[11px] text-gold-dark hover:underline">Open →</Link>
          </div>
          {data.paymentsTimeline.length === 0
            ? <div className="text-[12px] text-ink-3 text-center py-10">No payments captured in the last 30 days.</div>
            : <LineChart
                series={[
                  { label: 'Captured', data: paymentsCaptured, colour: '#10B981' },
                  { label: 'Refunded', data: paymentsRefunded, colour: '#EF4444' },
                ]}
                xLabels={paymentsX}
                yFormatter={fmtCents}
                height={220}
              />}
        </div>

        <div className="card">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="eyebrow !mb-1">Abandoned designs</div>
              <div className="text-ink font-semibold">Idle drafts by age</div>
            </div>
            <Link to="/admin/abandoned-carts" className="text-[11px] text-gold-dark hover:underline">Open →</Link>
          </div>
          {data.abandonedByAge.every((r) => r.count === 0)
            ? <div className="text-[12px] text-ink-3 text-center py-10">No abandoned drafts — every recent design was ordered or is still being edited.</div>
            : <BarChart data={abandonedBars} height={200} />}
        </div>
      </div>

      {/* Row: Top customers + Recent orders */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="eyebrow !mb-1">Top customers · 90 days</div>
              <div className="text-ink font-semibold">By paid revenue</div>
            </div>
            <Link to="/admin/customers" className="text-[11px] text-gold-dark hover:underline">All customers →</Link>
          </div>
          {data.topCustomers.length === 0 ? (
            <div className="text-[12px] text-ink-3 text-center py-10">No paid orders in the last 90 days.</div>
          ) : (
            <div className="space-y-2">
              {data.topCustomers.map((c) => {
                const max = Math.max(1, ...data.topCustomers.map((x) => x.totalCents))
                const pct = (c.totalCents / max) * 100
                return (
                  <div key={(c.email ?? '') + (c.name ?? '')} className="flex items-center gap-3 text-sm">
                    <div className="flex-1 min-w-0">
                      <div className="text-ink truncate">{c.name ?? c.email ?? '—'}</div>
                      <div className="text-[11px] text-ink-3 truncate">{c.email ?? '—'} · {c.orderCount} order{c.orderCount === 1 ? '' : 's'}</div>
                      <div className="h-1.5 rounded bg-surface mt-1.5 overflow-hidden">
                        <div className="h-full rounded" style={{ width: `${pct}%`, background: '#C8A96E' }} />
                      </div>
                    </div>
                    <div className="font-mono text-ink font-semibold text-sm w-20 text-right">{fmtCents(c.totalCents)}</div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="card">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="eyebrow !mb-1">Latest activity</div>
              <div className="text-ink font-semibold">10 most recent orders</div>
            </div>
            <Link to="/admin/orders" className="text-[11px] text-gold-dark hover:underline">All orders →</Link>
          </div>
          {data.recentOrders.length === 0 ? (
            <div className="text-[12px] text-ink-3 text-center py-10">No orders yet.</div>
          ) : (
            <div className="divide-y divide-border">
              {data.recentOrders.map((o) => (
                <Link key={o.id} to={`/admin/orders/${o.id}`}
                  className="flex items-center gap-3 py-2 hover:bg-surface transition-colors -mx-2 px-2 rounded">
                  <StatusBadge status={o.status} />
                  <div className="flex-1 min-w-0">
                    <div className="text-ink truncate text-sm">{o.customerName ?? o.customerEmail ?? '—'}</div>
                    <div className="text-[11px] text-ink-3 font-mono">#{o.number} · {o.itemCount} item{o.itemCount === 1 ? '' : 's'}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-semibold text-ink text-sm">{fmtCents(o.totalCents)}</div>
                    <div className="text-[11px] text-ink-3">{new Date(o.createdAt).toLocaleDateString()}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
